import { Modal } from 'react-bootstrap'
import { useEffect, useState, useRef, useCallback } from 'react'

const CustomModal = ({ children, show, ...props }) => {
  const [zIndex, setZIndex] = useState(1050)
  const modalRef = useRef(null)
  const backdropRef = useRef(null)
  const observerRef = useRef(null)

  const updateModalStack = useCallback(() => {
    const openModals = Array.from(document.querySelectorAll('.modal.show'))
    if (!openModals.length) return

    // Find our modal's index in the stack
    if (!modalRef.current) return
    const modalIndex = openModals.indexOf(modalRef.current)
    if (modalIndex === -1) return

    // Calculate new z-index based on position in stack
    const newZIndex = 1050 + modalIndex * 10
    setZIndex(newZIndex)

    if (backdropRef.current) {
      backdropRef.current.style.zIndex = `${newZIndex - 5}`
    }
  }, [])

  // Setup observer when component mounts
  useEffect(() => {
    observerRef.current = new MutationObserver((mutations) => {
      const relevantChanges = mutations.some(
        (mutation) =>
          Array.from(mutation.addedNodes).some(
            (node) => node.classList && (node.classList.contains('modal') || node.classList.contains('modal-backdrop'))
          ) ||
          Array.from(mutation.removedNodes).some(
            (node) => node.classList && (node.classList.contains('modal') || node.classList.contains('modal-backdrop'))
          )
      )

      if (relevantChanges) {
        updateModalStack()
      }
    })

    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    })

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [updateModalStack])

  useEffect(() => {
    if (show && modalRef.current) {
      // Look for the backdrop that was just added
      const findBackdrop = () => {
        const backdrops = Array.from(document.querySelectorAll('.modal-backdrop.show'))
        if (backdrops.length > 0) {
          backdropRef.current = backdrops[backdrops.length - 1]
          updateModalStack()
        }
      }

      // We need a slight delay to ensure the backdrop is in the DOM
      const timer = requestAnimationFrame(findBackdrop)
      return () => cancelAnimationFrame(timer)
    }
  }, [show, updateModalStack])

  const handleEntered = (e) => {
    if (props.onEntered) {
      props.onEntered(e)
    }
    updateModalStack()
  }

  return (
    <Modal ref={modalRef} show={show} {...props} style={{ zIndex }} onEntered={handleEntered}>
      {children}
    </Modal>
  )
}

export default CustomModal
